'use client';

import { SessionProvider, useSession, signOut } from 'next-auth/react';
import type { Session } from 'next-auth';
import { IdPathProvider } from '@/contexts/IdPathProvider';
import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { resolveAuthContext } from '@/utils/auth-context';

function SessionErrorHandler({ children }: { children: React.ReactNode }) {
    const { data: session } = useSession();
    const pathname = usePathname() || '';

    useEffect(() => {
        // Heritage và Public pages không cần xử lý session errors của admin
        const context = resolveAuthContext(pathname);
        if (context === 'HERITAGE' || context === 'PUBLIC') return;

        if ((session as any)?.error === 'RefreshAccessTokenError' && typeof window !== 'undefined') {
            // Clear all local storage states explicitly
            localStorage.removeItem('isLoggedIn');
            sessionStorage.clear();

            // Dispatch event to show beautiful toast on current screen.
            // Actual signOut + redirect is handled by SessionAuthGuard after a delay,
            // so the user has time to see this toast.
            if (!(window as any).__isLoggingOut) {
                (window as any).__isLoggingOut = true;
                window.dispatchEvent(new CustomEvent('session_expired_event'));
            }
        }
    }, [session, pathname]);

    return <>{children}</>;
}

import { setMemoryToken } from '@/utils/tokenManager';

function SessionSynchronizer() {
    const { data: session } = useSession();

    useEffect(() => {
        // Đồng bộ accessToken vào biến Memory (RAM)
        if (session && (session as any).accessToken) {
            setMemoryToken((session as any).accessToken);
        } else {
            setMemoryToken(null);
        }
    }, [session]);

    return null;
}

interface Props {
    children: React.ReactNode;
    session?: Session | null;
}

// Helper function to compute correct login URL based on current path
export function computeLoginUrlFromPath(pathname: string): string {
    if (typeof window === 'undefined') return '/admin/login';

    const context = resolveAuthContext(pathname);

    if (context === 'PROJECT_ADMIN') {
        const segments = pathname.split('/').filter(Boolean);
        const adminIndex = segments.indexOf('admin');
        if (adminIndex !== -1) {
            return `/${segments.slice(0, adminIndex + 1).join('/')}/login`;
        }
    }

    // Default fallback for ADMIN and PUBLIC
    return '/admin/login';
}

function SessionAuthGuard({ children }: { children: React.ReactNode }) {
    const { data: session } = useSession();
    const router = useRouter();
    const pathname = usePathname() || '';

    useEffect(() => {
        // ====== THÊM MỚI: Bỏ qua toàn bộ logic trên Heritage và Public pages ======
        const context = resolveAuthContext(pathname);
        if (context === 'HERITAGE' || context === 'PUBLIC') {
            return; // Heritage/Public không cần auth guard của admin
        }
        // ====== KẾT THÚC THÊM MỚI ======

        if (session?.error === 'RefreshAccessTokenError') {
            console.warn('Session expired (RefreshAccessTokenError). Signing out...');

            const loginUrl = computeLoginUrlFromPath(pathname);

            // Delay to let the toast dispatched by SessionErrorHandler be seen
            setTimeout(async () => {
                await signOut({ redirect: false });
                (window as any).__isLoggingOut = false;
                router.push(loginUrl + '?expired=true');
            }, 2500);
        } else if (session && typeof document !== 'undefined') {
            // Kiểm tra Cookie phụ (Dual-Cookie check)
            // Nếu có session nhưng mất frontend_session, nghĩa là user đã tắt trình duyệt
            // và họ không tick "Nhớ mặt" (vì cookie không có max-age).
            const hasFrontendSession = document.cookie.includes('frontend_session=active');
            const isLoginPage = pathname.includes('/login');

            if (!hasFrontendSession && !isLoginPage && !(window as any).__isLoggingOut) {
                console.warn('Browser restarted and remember me was false. Logging out...');
                (window as any).__isLoggingOut = true;
                
                const loginUrl = computeLoginUrlFromPath(pathname);

                signOut({ redirect: false }).then(() => {
                    (window as any).__isLoggingOut = false;
                    router.push(loginUrl + '?expired=true');
                });
            }
        }
    }, [session, pathname, router]);

    return <>{children}</>;
}

import { SessionManagerProvider } from '@/session-manager/SessionManagerProvider';

export default function NextAuthProvider({ children, session }: Props) {
    return (
        <SessionProvider session={session} refetchInterval={0} refetchOnWindowFocus={false}>
            <SessionSynchronizer />
            <SessionManagerProvider>
                <SessionErrorHandler>
                    <SessionAuthGuard>
                        <IdPathProvider>{children}</IdPathProvider>
                    </SessionAuthGuard>
                </SessionErrorHandler>
            </SessionManagerProvider>
        </SessionProvider>
    );
}
